/**
 * Offline Locator Verifier
 *
 * Verifies a `SelectorStrategy` against a trace-reconstructed DOM without
 * touching the live application — the same bar the scenario suite's locator
 * runner enforces (`apps/api/tests/scenarios/runner.locator.pw.ts`), lifted
 * into an importable library:
 *
 * 1. Load the reconstructed dom.html in a headless page and let the trace
 *    viewer's restore handlers settle.
 * 2. Materialize Playwright's internal aria-ref table via `ariaSnapshot`.
 * 3. Capture the ground-truth handle for the recorded target ref.
 * 4. Resolve the candidate strategy through the production `resolveElement`
 *    (verify mode) and require handle identity with the ground truth.
 * 5. Reject strategies that lean on volatile identifier tokens (seeded entity
 *    ids, framework-generated class hashes) via the shared classifier.
 *
 * A locator that fails here must degrade to a gap — never ship unverified.
 *
 * @module optimizer/locator-verifier
 */

import path from 'node:path';
import { pathToFileURL } from 'node:url';
import type { SelectorStrategy } from '@chatsdet/types';
import { isVolatileIdentifier, stableIdentifierPrefix } from '@chatsdet/types';
import { resolveElement } from '../element-resolver.js';
import { hydrateDynamicSelectors } from '../selector-derivation.js';
import { waitForTraceDomHydration } from './substrate-materializer.js';
import { HeadlessDomSession } from './headless-session.js';

export interface LocatorVerification {
  /** Strategy resolved to exactly one element. */
  resolvesUniquely: boolean;
  /** Resolved element is the recorded target (handle identity). */
  matchesTarget: boolean;
  /** No identifying token classified as volatile. */
  volatileTokenClean: boolean;
  /** Volatile tokens found, for the agent to reason about. */
  volatileTokens: string[];
  /** All three checks passed. */
  verdict: boolean;
  /** Failure detail when verdict is false. */
  reason?: string;
}

const RESOLVE_TIMEOUT_MS = 5_000;

/**
 * Collect the identifying string tokens a strategy leans on, for the volatile
 * classifier. Role/label/text predicates are human-facing content (volatility
 * there is the variable-binding system's job); this pass guards the
 * machine-facing anchors: test ids, css hints, automation ids, and raw
 * locator chains.
 */
function identifyingTokens(strategy: SelectorStrategy): string[] {
  const tokens: string[] = [];
  if (strategy.testId) tokens.push(strategy.testId);
  if (strategy.automationId) tokens.push(strategy.automationId);
  if (strategy.css) {
    // Class tokens and attribute values inside the css hint.
    for (const cls of strategy.css.matchAll(/\.([A-Za-z0-9_-]+)/g)) tokens.push(cls[1]!);
    for (const attr of strategy.css.matchAll(/\[[^\]=]+="([^"]+)"\]/g)) tokens.push(attr[1]!);
  }
  if (strategy.playwrightLocator) {
    for (const tid of strategy.playwrightLocator.matchAll(/getByTestId\('([^']+)'\)/g)) {
      tokens.push(tid[1]!);
    }
  }
  return tokens;
}

export function classifyStrategyTokens(strategy: SelectorStrategy): {
  volatileTokenClean: boolean;
  volatileTokens: string[];
} {
  const volatileTokens = identifyingTokens(strategy).filter(
    // A token with a stable prefix (e.g. `__new__<ts>`) is usable via prefix
    // matching — only fully-volatile tokens fail the pass.
    (t) => isVolatileIdentifier(t) && stableIdentifierPrefix(t) === null
  );
  return { volatileTokenClean: volatileTokens.length === 0, volatileTokens };
}

export interface VerifyLocatorOptions {
  /** Path to the trace-reconstructed, self-contained dom.html. */
  domPath: string;
  /** The recorded target's aria-ref in the substrate captured over this DOM. */
  targetRef: string;
  strategy: SelectorStrategy;
  /** Variable values for hydrating dynamic selectors before resolution. */
  variableValues?: Record<string, string>;
}

/**
 * Shared-browser verifier bound to one optimization session. `close()` must
 * run when the session ends.
 */
export class LocatorVerifier {
  private readonly session: HeadlessDomSession;
  private readonly ownsSession: boolean;

  constructor(opts?: { session?: HeadlessDomSession }) {
    this.session = opts?.session ?? new HeadlessDomSession();
    this.ownsSession = !opts?.session;
  }

  async verify(opts: VerifyLocatorOptions): Promise<LocatorVerification> {
    const tokenPass = classifyStrategyTokens(opts.strategy);

    const page = await this.session.page();
    try {
      await page.goto(pathToFileURL(path.resolve(opts.domPath)).href, { waitUntil: 'load' });
      await waitForTraceDomHydration(page);

      // Materialize Playwright's internal ref table for this page so the
      // `aria-ref=` selector engine can resolve refs the substrate yaml points
      // at. ariaSnapshot itself does not write a DOM `aria-ref` attribute;
      // refs only exist within Playwright's snapshot engine.
      await page.ariaSnapshot({ mode: 'ai' });

      // Ground-truth handle immediately after the snapshot that created the
      // ref table — later locator probes can refresh refs.
      const truth = page.locator(`aria-ref=${opts.targetRef}`);
      const truthHandle = await truth.elementHandle({ timeout: RESOLVE_TIMEOUT_MS }).catch(() => null);
      if (!truthHandle) {
        return {
          resolvesUniquely: false,
          matchesTarget: false,
          ...tokenPass,
          verdict: false,
          reason: `target ref ${opts.targetRef} not present in the reconstructed DOM`,
        };
      }

      const hydrated =
        opts.variableValues && Object.keys(opts.variableValues).length > 0
          ? hydrateDynamicSelectors(opts.strategy, opts.variableValues)
          : opts.strategy;

      const resolved = await resolveElement(page, hydrated, RESOLVE_TIMEOUT_MS, {
        mode: 'verify',
      }).catch(() => null);
      if (!resolved) {
        await truthHandle.dispose().catch(() => undefined);
        return {
          resolvesUniquely: false,
          matchesTarget: false,
          ...tokenPass,
          verdict: false,
          reason: 'strategy did not resolve to a unique element',
        };
      }

      const resolvedHandle = await resolved
        .elementHandle({ timeout: RESOLVE_TIMEOUT_MS })
        .catch(() => null);
      let matchesTarget = false;
      try {
        if (resolvedHandle) {
          matchesTarget = await page.evaluate(
            ([a, b]) => a === b,
            [resolvedHandle, truthHandle] as const
          );
        }
      } finally {
        await resolvedHandle?.dispose().catch(() => undefined);
        await truthHandle.dispose().catch(() => undefined);
      }

      const verdict = matchesTarget && tokenPass.volatileTokenClean;
      return {
        resolvesUniquely: true,
        matchesTarget,
        ...tokenPass,
        verdict,
        reason: verdict
          ? undefined
          : !matchesTarget
            ? 'strategy resolved a different element than the recorded target'
            : `volatile identifier tokens: ${tokenPass.volatileTokens.join(', ')}`,
      };
    } finally {
      await page.close().catch(() => undefined);
    }
  }

  async close(): Promise<void> {
    if (this.ownsSession) await this.session.close();
  }
}
